/**
 * Staff de Habitia desde SuperAdmin: administradores (typeuser 1) y comerciales
 * (typeuser 4). GestionComerciales.jsx usa este mismo componente (`GestionStaff`)
 * con tipo="comercial".
 *
 * Fase 0C de seguridad: el alta, la edición y la baja pasan por los callables
 * createStaffUser / updateStaffUser / removeStaffUser (functions/identity/staff.js).
 * El navegador ya no escribe `users` ni crea o borra cuentas de Auth:
 *  - Alta: el servidor crea la cuenta SIN contraseña (o reusa la del email si la
 *    persona ya tenía otro rol) y, si la respuesta trae definirContrasena, acá se
 *    manda el correo de Firebase para que la defina. Si la cuenta del email se
 *    activó con un link anterior al sistema nuevo (D3), el servidor pide
 *    confirmarlo y acá se pregunta antes de reintentar con confirmarLinkEnClaro.
 *  - Edición: formulario con botón Guardar que manda solo los campos cambiados
 *    (antes se guardaba campo por campo con cada tecla).
 *  - Baja: el servidor archiva la ficha (archivedAt) y deshabilita la cuenta si
 *    no tiene otros roles. Los users nunca se borran: la lista oculta los archivados.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import {
  FaUserPlus,
  FaTrash,
  FaUsers,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaUser,
  FaUserShield,
  FaUserTie,
  FaEye,
  FaPen,
} from "react-icons/fa";
import { db, auth } from "../../firebase";
import AdminModal from "../../components/AdminModal";
import DataTable from "../../components/DataTable";
import FormField from "../../components/FormField";
import Loader from "../../components/Loader";
import ScreenHeader from "../../components/ScreenHeader";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import {
  createStaffUser,
  updateStaffUser,
  removeStaffUser,
  nuevoRequestId,
  mensajeDeError,
  sinMensajeDelServidor,
  codigoDeError,
} from "../../utils/identityApi";
import { getOptionalPasswordResetActionCodeSettings } from "../../utils/authEmailActions";
import {
  DOC_TYPES,
  isNumericDocType,
  normalizeDocumento,
  getDocumentoValidationMessage,
} from "../../utils/documento";
import { formatDocumentoDisplay } from "../../utils/documentoFormat";
import {
  colors,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  inputBase,
  pageContainer,
} from "../../utils/designTokens";

// ─── Configuración por tipo de staff ─────────────────────────────────────────
const TIPOS = {
  admin: {
    tipo: "admin",
    typeusers: [1],
    singular: "administrador",
    plural: "administradores",
    titulo: "Administradores",
    subtitulo: "Gestión de administradores",
    icono: FaUserShield,
    bajaDetalle: "Pierde el acceso como administrador y se lo quita de sus edificios.",
  },
  comercial: {
    tipo: "comercial",
    // Como el Number(typeuser) === 4 de antes: por si alguna ficha lo guardó como string.
    typeusers: [4, "4"],
    singular: "comercial",
    plural: "comerciales",
    titulo: "Comerciales",
    subtitulo: "Gestión de usuarios comerciales (acceso a Prospección)",
    icono: FaUserTie,
    bajaDetalle: "Pierde el acceso como comercial. Sus leads del CRM no se tocan: se liberan solos a los 60 días.",
  },
};

const FORM_VACIO = {
  NombreCompleto: "",
  ApellidosCompleto: "",
  tipoDocumento: "CI",
  Documento: "",
  email: "",
  celular: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normEmail = (v) => String(v || "").trim().toLowerCase();
const recortar = (v) => String(v == null ? "" : v).trim();

/** Ficha viva: ni archivada (baja) ni fusionada con otra. */
const vigente = (u) => !u.archivedAt && u.mergedIntoUserid == null;

const nombreDe = (u) => {
  const completo = u.NombreCompleto ? `${u.NombreCompleto} ${u.ApellidosCompleto || ""}` : u.name || "";
  return completo.replace(/\s+/g, " ").trim() || u.email || "esta persona";
};

/** Valores con que arranca el formulario de edición (los mismos que mostraba la tabla). */
const fichaAForm = (u) => ({
  NombreCompleto: String(u.NombreCompleto || u.name || ""),
  ApellidosCompleto: String(u.ApellidosCompleto || ""),
  tipoDocumento: DOC_TYPES.includes(u.tipoDocumento) ? u.tipoDocumento : "CI",
  Documento: String(u.Documento || u.ci || ""),
  email: String(u.email || ""),
  celular: String(u.celular || ""),
});

/**
 * Campos que el operador cambió, ya normalizados: es lo que recibe
 * updateStaffUser (el contrato pide mandar solo lo que cambió).
 */
function camposEditados(inicial, form, original) {
  const editado = (k) => recortar(form[k]) !== recortar(inicial[k]);
  const campos = {};
  if (editado("NombreCompleto")) campos.NombreCompleto = recortar(form.NombreCompleto);
  if (editado("ApellidosCompleto")) {
    campos.ApellidosCompleto = recortar(form.ApellidosCompleto);
    // Ficha vieja sin NombreCompleto (el formulario lo tomó de `name`): el
    // servidor arma `name` con los dos, así que va también para no perderlo.
    if (!original.NombreCompleto && recortar(form.NombreCompleto)) {
      campos.NombreCompleto = recortar(form.NombreCompleto);
    }
  }
  if (form.tipoDocumento !== inicial.tipoDocumento) campos.tipoDocumento = form.tipoDocumento;
  if (editado("Documento")) campos.Documento = normalizeDocumento(form.Documento, form.tipoDocumento);
  if (editado("email")) campos.email = normEmail(form.email);
  if (editado("celular")) campos.celular = recortar(form.celular);
  return campos;
}

const validarAlta = (f) => {
  if (
    !recortar(f.NombreCompleto) ||
    !recortar(f.ApellidosCompleto) ||
    !recortar(f.Documento) ||
    !recortar(f.email) ||
    !recortar(f.celular)
  ) {
    return "Por favor completá todos los campos requeridos.";
  }
  const errDoc = getDocumentoValidationMessage(f.tipoDocumento, f.Documento);
  if (errDoc) return errDoc;
  if (!EMAIL_RE.test(normEmail(f.email))) return "Por favor ingresá un email válido.";
  return "";
};

/** Ningún campo se puede vaciar (contrato de updateStaffUser). */
const validarEdicion = (campos, form) => {
  const obligatorios = {
    NombreCompleto: "Completá el nombre.",
    ApellidosCompleto: "Completá los apellidos.",
    email: "Completá el email.",
    celular: "Completá el celular.",
  };
  for (const [k, mensaje] of Object.entries(obligatorios)) {
    if (k in campos && !recortar(campos[k])) return mensaje;
  }
  if ("Documento" in campos || "tipoDocumento" in campos) {
    const errDoc = getDocumentoValidationMessage(form.tipoDocumento, form.Documento);
    if (errDoc) return errDoc;
  }
  if ("email" in campos && !EMAIL_RE.test(campos.email)) return "Por favor ingresá un email válido.";
  return "";
};

// ─── Callables: confirmaciones, errores y correo de contraseña ───────────────
/** failed-precondition que el servidor deja confirmar → flag con que se reintenta. */
const CONFIRMACIONES = {
  celular_compartido: "confirmarCompartido",
  edificios_sin_admin: "confirmarSinAdmins",
  // D3: la cuenta de ese email se activó con un link anterior al sistema nuevo.
  confirmar_link_en_claro: "confirmarLinkEnClaro",
};

/** Qué hacer si la persona no reconoce la cuenta: recuperarla con su email antes del alta. */
const NOTA_LINK_EN_CLARO =
  "Si no la reconoce, no sigas: que entre con «Olvidé mi contraseña» con su email y después repetí el alta.";

const motivoDe = (e) => (e && e.details && typeof e.details === "object" ? e.details.motivo : undefined);

const flagDeConfirmacion = (e) =>
  codigoDeError(e) === "failed-precondition" ? CONFIRMACIONES[motivoDe(e)] || null : null;

/** Errores del SDK sin mensaje del servidor (corte de red, timeout): se aclara que reintentar no repite. */
const mensajeLegible = (e, fallback) =>
  sinMensajeDelServidor(e)
    ? `${fallback} Revisá la conexión y volvé a intentar: si ya se había hecho, no se repite.`
    : mensajeDeError(e, fallback);

/** Correo de Firebase para que la persona elija su contraseña (el mismo que mandaba el alta anterior). */
async function enviarDefinirContrasena(email) {
  const ajustes = getOptionalPasswordResetActionCodeSettings();
  if (ajustes) await sendPasswordResetEmail(auth, email, ajustes);
  else await sendPasswordResetEmail(auth, email);
}

const motivoEnvio = (e) => {
  const code = String((e && e.code) || "");
  if (code === "auth/too-many-requests") return "demasiados intentos seguidos";
  if (code === "auth/network-request-failed") return "sin conexión";
  if (code === "auth/user-disabled") return "la cuenta está deshabilitada";
  return code.replace(/^auth\//, "") || "error desconocido";
};

const debeDefinirContrasena = (res) =>
  typeof res.definirContrasena === "boolean" ? res.definirContrasena : res.authCreada === true;

/** Estado de la cuenta Auth después de la baja (removeStaffUser.auth). */
const NOTA_AUTH_BAJA = {
  deshabilitada: "Su cuenta de acceso quedó deshabilitada.",
  sesiones_revocadas: "Conserva su cuenta por sus otros roles en Habitia; se cerraron sus sesiones abiertas.",
  sin_cambios: "Su cuenta de acceso no se modificó.",
  sin_cuenta: "",
  error: "Su cuenta de acceso quedó sin actualizar: si sigue activa, pedile a soporte que la revise.",
};

const ListaEdificios = ({ edificios }) => (
  <ul style={styles.listaEdificios}>
    {edificios.map((e) => (
      <li key={String(e.buildingId)}>{e.nombre || `Edificio ${e.buildingId}`}</li>
    ))}
  </ul>
);

// ─── Componente ──────────────────────────────────────────────────────────────
export const GestionStaff = ({ tipo = "admin", setSelected, setAdminVistaSoporteDocId }) => {
  const cfg = TIPOS[tipo] || TIPOS.admin;
  const { showToast } = useToast();
  const { confirmDialog, alertDialog } = useAppDialog();

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevo, setNuevo] = useState(FORM_VACIO);
  const [edicion, setEdicion] = useState(null); // { original, inicial, form }
  // Una operación a la vez ("alta", "edicion:<id>", "baja:<id>"): el diálogo de
  // confirmación es uno solo para toda la app.
  const [operacion, setOperacion] = useState(null);

  // requestId por intento: se reusa mientras los datos no cambien (reintento tras
  // una confirmación o un corte: el servidor no repite la operación) y se
  // descarta cuando la operación terminó bien.
  const intentos = useRef({});
  const requestIdPara = useCallback((clave, firma) => {
    const previo = intentos.current[clave];
    if (previo && previo.firma === firma) return previo.requestId;
    const requestId = nuevoRequestId();
    intentos.current[clave] = { firma, requestId };
    return requestId;
  }, []);
  const cerrarIntento = useCallback((clave) => {
    delete intentos.current[clave];
  }, []);

  const cargar = useCallback(async () => {
    try {
      const snap = await getDocs(query(collection(db, "users"), where("typeuser", "in", cfg.typeusers)));
      setLista(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(vigente));
    } catch (e) {
      console.error(`Error cargando ${cfg.plural}:`, e);
      showToast(`No se pudo cargar la lista de ${cfg.plural}.`, "error");
    } finally {
      setLoading(false);
    }
  }, [cfg, showToast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /** Pregunta lo que el servidor pide confirmar (celular compartido, edificios sin admin, link anterior). */
  const preguntar = useCallback(
    (e) => {
      if (motivoDe(e) === "confirmar_link_en_claro") {
        const msg = mensajeDeError(e, "La cuenta de ese email se activó con un link anterior al sistema nuevo y puede no ser de esta persona.");
        return confirmDialog(`${msg}\n\n${NOTA_LINK_EN_CLARO}`, {
          title: "Cuenta activada con un link anterior",
          confirmLabel: "Ya la verifiqué, continuar",
          danger: true,
        });
      }
      if (motivoDe(e) === "edificios_sin_admin") {
        const edificios = Array.isArray(e.details.edificiosSinAdmin) ? e.details.edificiosSinAdmin : [];
        return confirmDialog(mensajeDeError(e), {
          title: "Edificios sin administrador",
          confirmLabel: "Dar de baja igual",
          danger: true,
          content: edificios.length ? <ListaEdificios edificios={edificios} /> : null,
        });
      }
      return confirmDialog(mensajeDeError(e), {
        title: "Celular compartido",
        confirmLabel: "Continuar igual",
      });
    },
    [confirmDialog]
  );

  /**
   * Llama al callable; si responde un failed-precondition confirmable, pregunta
   * y reintenta con el flag y el MISMO requestId. null = el operador canceló.
   */
  const llamar = useCallback(
    async (fn, datos) => {
      let intento = datos;
      for (;;) {
        try {
          return await fn(intento);
        } catch (e) {
          const flag = flagDeConfirmacion(e);
          if (!flag || intento[flag]) throw e;
          if (!(await preguntar(e))) return null;
          intento = { ...intento, [flag]: true };
        }
      }
    },
    [preguntar]
  );

  const mostrarError = useCallback(
    (e, fallback) => {
      console.error(fallback, e);
      const codigo = codigoDeError(e);
      if (codigo === "aborted") {
        // No es un fallo: la misma operación sigue corriendo en el servidor.
        showToast(mensajeDeError(e, "Esa operación ya está en curso. Esperá unos segundos y revisá."), "info");
        cargar();
        return;
      }
      showToast(mensajeLegible(e, fallback), "error");
      if (codigo === "not-found" || codigo === "failed-precondition") cargar();
    },
    [cargar, showToast]
  );

  // ── Alta ──
  const handleAdd = async () => {
    if (operacion) return;
    const error = validarAlta(nuevo);
    if (error) {
      showToast(error, "error");
      return;
    }
    const email = normEmail(nuevo.email);
    const payload = {
      tipo: cfg.tipo,
      NombreCompleto: recortar(nuevo.NombreCompleto),
      ApellidosCompleto: recortar(nuevo.ApellidosCompleto),
      tipoDocumento: nuevo.tipoDocumento,
      Documento: normalizeDocumento(nuevo.Documento, nuevo.tipoDocumento),
      email,
      celular: recortar(nuevo.celular),
    };
    setOperacion("alta");
    try {
      const res = await llamar(createStaffUser, {
        ...payload,
        requestId: requestIdPara("alta", JSON.stringify(payload)),
      });
      if (!res) return;
      cerrarIntento("alta");
      setNuevo(FORM_VACIO);

      const avisos = [];
      let mensaje;
      if (debeDefinirContrasena(res)) {
        try {
          await enviarDefinirContrasena(email);
          mensaje = `Listo. Le enviamos un correo a ${email} para que cree su contraseña de acceso a Habitia (revisá spam si no lo ve).`;
        } catch (envioErr) {
          console.error("sendPasswordResetEmail:", envioErr);
          avisos.push(
            `Se creó el ${cfg.singular}, pero no pudimos enviarle el correo para que cree su contraseña (${motivoEnvio(envioErr)}). ` +
              `Puede crearla desde «Olvidé mi contraseña» en la pantalla de ingreso, con el email ${email}.`
          );
        }
      } else {
        mensaje = `Listo. ${email} ya tenía cuenta en Habitia: entra con su email y la contraseña que ya usa.`;
      }
      if (res.cuentaReactivada) {
        const nota = "Su cuenta estaba deshabilitada y se reactivó.";
        if (mensaje) mensaje = `${mensaje} ${nota}`;
        else avisos.push(nota);
      }
      if (res.cuentaDeshabilitada) {
        avisos.push(
          `Su cuenta de acceso sigue deshabilitada: no va a poder entrar hasta que se reactive. Pedile a soporte que la revise.`
        );
      }
      await cargar();
      if (avisos.length) {
        alertDialog([mensaje, ...avisos].filter(Boolean).join("\n\n"), { title: "Revisá el alta" });
      } else {
        showToast(mensaje, "success");
      }
    } catch (e) {
      mostrarError(e, `No se pudo crear el ${cfg.singular}.`);
    } finally {
      setOperacion(null);
    }
  };

  // ── Edición ──
  const abrirEdicion = (u) => {
    if (operacion) return;
    const inicial = fichaAForm(u);
    setEdicion({ original: u, inicial, form: { ...inicial } });
  };

  const setCampoEdicion = (campo, valor) =>
    setEdicion((ed) => (ed ? { ...ed, form: { ...ed.form, [campo]: valor } } : ed));

  const cerrarEdicion = () => {
    if (!operacion) setEdicion(null);
  };

  const camposPendientes = edicion ? camposEditados(edicion.inicial, edicion.form, edicion.original) : {};
  const hayCambios = Object.keys(camposPendientes).length > 0;
  const guardandoEdicion = !!edicion && operacion === `edicion:${edicion.original.id}`;

  const guardarEdicion = async () => {
    if (!edicion || operacion) return;
    const { original, form } = edicion;
    const campos = camposPendientes;
    if (!Object.keys(campos).length) {
      setEdicion(null);
      showToast("No hay cambios para guardar.", "info");
      return;
    }
    const error = validarEdicion(campos, form);
    if (error) {
      showToast(error, "error");
      return;
    }
    const nombre = nombreDe(original);
    if ("email" in campos && campos.email !== normEmail(original.email)) {
      const ok = await confirmDialog(
        `${nombre} va a entrar a Habitia con ${campos.email}` +
          `${original.email ? ` en lugar de ${original.email}` : ""}, con la misma contraseña, ` +
          "y se le cierran las sesiones abiertas. ¿Continuar?",
        { title: "Cambiar email de acceso", confirmLabel: "Cambiar email" }
      );
      if (!ok) return;
    }
    const clave = `edicion:${original.id}`;
    setOperacion(clave);
    try {
      const res = await llamar(updateStaffUser, {
        userDocId: original.id,
        campos,
        requestId: requestIdPara(clave, JSON.stringify(campos)),
      });
      if (!res) return;
      cerrarIntento(clave);
      setEdicion(null);

      const emailNuevo = campos.email || normEmail(original.email);
      const avisos = [];
      let mensaje = Array.isArray(res.cambios) && res.cambios.length === 0
        ? "No había cambios para guardar."
        : "Cambios guardados.";
      if (res.emailCambiado) {
        mensaje += ` Ahora entra a Habitia con ${emailNuevo}${res.definirContrasena ? "" : " y la misma contraseña"}.`;
        if (res.sesionesRevocadas) mensaje += " Se cerraron sus sesiones abiertas.";
      }
      if (res.definirContrasena) {
        try {
          await enviarDefinirContrasena(emailNuevo);
          mensaje += ` Le enviamos un correo a ${emailNuevo} para que cree su contraseña (revisá spam si no lo ve).`;
        } catch (envioErr) {
          console.error("sendPasswordResetEmail:", envioErr);
          avisos.push(
            `No pudimos enviarle el correo para que cree su contraseña (${motivoEnvio(envioErr)}). ` +
              `Puede crearla desde «Olvidé mi contraseña» en la pantalla de ingreso, con el email ${emailNuevo}.`
          );
        }
      }
      await cargar();
      if (avisos.length) alertDialog([mensaje, ...avisos].join("\n\n"), { title: "Revisá los cambios" });
      else showToast(mensaje, res.cambios && res.cambios.length === 0 ? "info" : "success");
    } catch (e) {
      // Usuario borrado, dado de baja o con la cuenta rota: el formulario quedó viejo.
      if (["not-found", "failed-precondition"].includes(codigoDeError(e))) setEdicion(null);
      mostrarError(e, "No se pudieron guardar los cambios.");
    } finally {
      setOperacion(null);
    }
  };

  // ── Baja ──
  const darDeBaja = async (u) => {
    if (operacion) return;
    const nombre = nombreDe(u);
    const ok = await confirmDialog(
      `¿Dar de baja a ${nombre}? ${cfg.bajaDetalle} Su ficha se archiva: no se borra.`,
      { title: `Dar de baja ${cfg.singular}`, confirmLabel: "Dar de baja", danger: true }
    );
    if (!ok) return;
    const clave = `baja:${u.id}`;
    setOperacion(clave);
    try {
      const primera = await llamar(removeStaffUser, { userDocId: u.id, requestId: requestIdPara(clave, u.id) });
      if (!primera) return;
      cerrarIntento(clave);
      setLista((prev) => prev.filter((x) => x.id !== u.id));

      // La baja quedó hecha pero falló el paso de Auth: reintentar es seguro
      // (es otra operación, así que va con un requestId nuevo). Del reintento solo
      // importa `auth`: los links de admin ya no están y los edificios sin admin
      // y los leads salen de la primera respuesta.
      let res = primera;
      while (res.auth === "error") {
        const reintentar = await confirmDialog(
          `Se dio de baja a ${nombre}, pero no pudimos actualizar su cuenta de acceso ` +
            "(deshabilitarla o cerrar sus sesiones). Reintentarlo es seguro.",
          { title: "Cuenta de acceso sin actualizar", confirmLabel: "Reintentar", cancelLabel: "Más tarde" }
        );
        if (!reintentar) break;
        try {
          res = (await llamar(removeStaffUser, { userDocId: u.id, requestId: nuevoRequestId() })) || res;
        } catch (e) {
          console.error("Reintento de baja (Auth):", e);
          showToast(mensajeLegible(e, "No se pudo actualizar su cuenta de acceso."), "error");
        }
      }

      const mensaje = [`Se dio de baja a ${nombre}.`, NOTA_AUTH_BAJA[res.auth] || ""].filter(Boolean).join(" ");
      const extras = [];
      if (Array.isArray(primera.edificiosSinAdmin) && primera.edificiosSinAdmin.length) {
        extras.push(
          `Quedaron sin administrador: ${primera.edificiosSinAdmin.map((e) => e.nombre || `Edificio ${e.buildingId}`).join(", ")}.`
        );
      }
      const leads = primera.leadsAsignados;
      if (typeof leads === "number" && leads > 0) {
        extras.push(
          `Tenía ${leads} ${leads === 1 ? "lead asignado" : "leads asignados"} en el CRM: ` +
            "no se tocan y se liberan solos a los 60 días."
        );
      }
      if (extras.length || res.auth === "error") {
        alertDialog([mensaje, ...extras].join("\n\n"), { title: "Baja hecha" });
      } else {
        showToast(mensaje, "success");
      }
    } catch (e) {
      mostrarError(e, `No se pudo dar de baja al ${cfg.singular}.`);
    } finally {
      setOperacion(null);
    }
  };

  const verSoporte =
    cfg.tipo === "admin" && setAdminVistaSoporteDocId && setSelected
      ? (u) => {
          setAdminVistaSoporteDocId(u.id);
          setSelected("adminsoporte");
        }
      : null;

  if (loading) return <Loader />;

  const ocupado = !!operacion;
  const botonAccion = (base) => (ocupado ? { ...base, ...styles.botonDeshabilitado } : base);

  const columnas = [
    { key: "NombreCompleto", label: "Nombre", render: (_, u) => u.NombreCompleto || u.name || "—" },
    { key: "ApellidosCompleto", label: "Apellidos", render: (v) => v || "—" },
    { key: "Documento", label: "Documento", render: (_, u) => formatDocumentoDisplay(u) },
    { key: "email", label: "Email", render: (v) => v || "—" },
    { key: "celular", label: "Celular", render: (v) => v || "—" },
    {
      key: "acciones",
      label: "Acciones",
      width: verSoporte ? "140px" : "100px",
      render: (_, u) => (
        <div style={styles.acciones}>
          {verSoporte && (
            <button
              type="button"
              onClick={() => verSoporte(u)}
              style={botonAccion(styles.eyeBtn)}
              className="habitia-btn-ghost"
              title="Ver qué ve este admin (soporte)"
              aria-label="Ver qué ve este admin (soporte)"
              disabled={ocupado}
            >
              <FaEye size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => abrirEdicion(u)}
            style={botonAccion(styles.editBtn)}
            className="habitia-btn-ghost"
            title="Editar"
            aria-label="Editar"
            disabled={ocupado}
          >
            <FaPen size={13} />
          </button>
          <button
            type="button"
            onClick={() => darDeBaja(u)}
            style={botonAccion(styles.deleteBtn)}
            className="habitia-btn-danger"
            title="Dar de baja"
            aria-label="Eliminar"
            disabled={ocupado}
          >
            <FaTrash size={14} />
          </button>
        </div>
      ),
    },
  ];

  const docNumerico = isNumericDocType(nuevo.tipoDocumento);
  const editNumerico = edicion ? isNumericDocType(edicion.form.tipoDocumento) : true;

  return (
    <div style={styles.page}>
      <ScreenHeader icon={cfg.icono} title={cfg.titulo} subtitle={cfg.subtitulo} />

      {/* Formulario de alta */}
      <div style={styles.formCard}>
        <h3 style={styles.formTitle}>
          <FaUserPlus size={16} color={colors.primary} />
          Agregar nuevo {cfg.singular}
        </h3>
        <div style={styles.formGrid}>
          <FormField label={<><FaUser size={12} /> Nombre</>} htmlFor="staff-alta-nombre" required>
            <input
              id="staff-alta-nombre"
              className="habitia-input"
              style={styles.input}
              placeholder="Nombre completo"
              value={nuevo.NombreCompleto}
              onChange={(e) => setNuevo((f) => ({ ...f, NombreCompleto: e.target.value }))}
            />
          </FormField>
          <FormField label={<><FaUser size={12} /> Apellidos</>} htmlFor="staff-alta-apellidos" required>
            <input
              id="staff-alta-apellidos"
              className="habitia-input"
              style={styles.input}
              placeholder="Apellidos completos"
              value={nuevo.ApellidosCompleto}
              onChange={(e) => setNuevo((f) => ({ ...f, ApellidosCompleto: e.target.value }))}
            />
          </FormField>
          <FormField label="Tipo de documento" htmlFor="staff-alta-tipodoc" required>
            <select
              id="staff-alta-tipodoc"
              className="habitia-input"
              style={styles.input}
              value={nuevo.tipoDocumento}
              onChange={(e) => setNuevo((f) => ({ ...f, tipoDocumento: e.target.value }))}
            >
              {DOC_TYPES.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </FormField>
          <FormField label={<><FaIdCard size={12} /> Documento</>} htmlFor="staff-alta-documento" required>
            <input
              id="staff-alta-documento"
              className="habitia-input"
              style={styles.input}
              placeholder={docNumerico ? "Ej: 12345678 (sin puntos ni guiones)" : "Ej: AB123456"}
              value={nuevo.Documento}
              onChange={(e) =>
                setNuevo((f) => ({ ...f, Documento: normalizeDocumento(e.target.value, f.tipoDocumento) }))
              }
              inputMode={docNumerico ? "numeric" : "text"}
            />
          </FormField>
          <FormField
            label={<><FaEnvelope size={12} /> Email</>}
            htmlFor="staff-alta-email"
            required
            hint={`Al crear el ${cfg.singular}, si todavía no tiene contraseña en Habitia, le enviamos un correo a esa dirección para que la defina.`}
          >
            <input
              id="staff-alta-email"
              className="habitia-input"
              style={styles.input}
              placeholder="email@ejemplo.com"
              type="email"
              inputMode="email"
              value={nuevo.email}
              onChange={(e) => setNuevo((f) => ({ ...f, email: e.target.value }))}
            />
          </FormField>
          <FormField label={<><FaPhone size={12} /> Celular</>} htmlFor="staff-alta-celular" required>
            <input
              id="staff-alta-celular"
              className="habitia-input"
              style={styles.input}
              placeholder="+598 99 123 456"
              type="tel"
              inputMode="tel"
              value={nuevo.celular}
              onChange={(e) => setNuevo((f) => ({ ...f, celular: e.target.value }))}
            />
          </FormField>
          <div style={styles.addCell}>
            <button
              type="button"
              style={ocupado ? { ...styles.addBtn, ...styles.botonDeshabilitado } : styles.addBtn}
              className="habitia-btn-primary"
              onClick={handleAdd}
              disabled={ocupado}
            >
              <FaUserPlus size={14} />
              {operacion === "alta" ? "Creando..." : "Agregar"}
            </button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <DataTable
        columns={columnas}
        data={lista}
        emptyIcon={FaUsers}
        emptyTitle={`No hay ${cfg.plural} registrados`}
      />

      {/* Edición: formulario con botón Guardar (antes se guardaba por tecla) */}
      <AdminModal
        open={!!edicion}
        onClose={cerrarEdicion}
        title={`Editar ${cfg.singular}`}
        subtitle={edicion ? nombreDe(edicion.original) : ""}
        size="medium"
        icon={<FaPen />}
        hideClose={guardandoEdicion}
        footer={
          <>
            <button
              type="button"
              onClick={cerrarEdicion}
              style={guardandoEdicion ? { ...styles.cancelBtn, ...styles.botonDeshabilitado } : styles.cancelBtn}
              className="habitia-btn-secondary"
              disabled={guardandoEdicion}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={guardarEdicion}
              style={!hayCambios || ocupado ? { ...styles.saveBtn, ...styles.botonDeshabilitado } : styles.saveBtn}
              className="habitia-btn-primary"
              disabled={!hayCambios || ocupado}
            >
              {guardandoEdicion ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        {edicion && (
          <div style={styles.modalGrid}>
            <FormField label="Nombre" htmlFor="staff-edit-nombre" required>
              <input
                id="staff-edit-nombre"
                className="habitia-input"
                style={inputBase}
                value={edicion.form.NombreCompleto}
                onChange={(e) => setCampoEdicion("NombreCompleto", e.target.value)}
                disabled={guardandoEdicion}
              />
            </FormField>
            <FormField label="Apellidos" htmlFor="staff-edit-apellidos" required>
              <input
                id="staff-edit-apellidos"
                className="habitia-input"
                style={inputBase}
                value={edicion.form.ApellidosCompleto}
                onChange={(e) => setCampoEdicion("ApellidosCompleto", e.target.value)}
                disabled={guardandoEdicion}
              />
            </FormField>
            <FormField label="Tipo de documento" htmlFor="staff-edit-tipodoc" required>
              <select
                id="staff-edit-tipodoc"
                className="habitia-input"
                style={inputBase}
                value={edicion.form.tipoDocumento}
                onChange={(e) => setCampoEdicion("tipoDocumento", e.target.value)}
                disabled={guardandoEdicion}
              >
                {DOC_TYPES.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Documento" htmlFor="staff-edit-documento" required>
              <input
                id="staff-edit-documento"
                className="habitia-input"
                style={inputBase}
                value={edicion.form.Documento}
                onChange={(e) =>
                  setCampoEdicion("Documento", normalizeDocumento(e.target.value, edicion.form.tipoDocumento))
                }
                inputMode={editNumerico ? "numeric" : "text"}
                disabled={guardandoEdicion}
              />
            </FormField>
            <FormField
              label="Email"
              htmlFor="staff-edit-email"
              required
              fullWidth
              hint="Es el email con el que entra a Habitia. Si lo cambiás, entra con el nuevo y la misma contraseña."
            >
              <input
                id="staff-edit-email"
                className="habitia-input"
                style={inputBase}
                type="email"
                inputMode="email"
                value={edicion.form.email}
                onChange={(e) => setCampoEdicion("email", e.target.value)}
                disabled={guardandoEdicion}
              />
            </FormField>
            <FormField label="Celular" htmlFor="staff-edit-celular" required>
              <input
                id="staff-edit-celular"
                className="habitia-input"
                style={inputBase}
                type="tel"
                inputMode="tel"
                placeholder="+598 99 123 456"
                value={edicion.form.celular}
                onChange={(e) => setCampoEdicion("celular", e.target.value)}
                disabled={guardandoEdicion}
              />
            </FormField>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

const GestionAdmins = ({ setSelected, setAdminVistaSoporteDocId }) => (
  <GestionStaff tipo="admin" setSelected={setSelected} setAdminVistaSoporteDocId={setAdminVistaSoporteDocId} />
);

// ─── Estilos con design tokens ───────────────────────────────────────────────
const botonIcono = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${spacing[2]}px ${spacing[3]}px`,
  borderRadius: radius.sm,
  cursor: "pointer",
  transition: `all ${transitions.fast}`,
};

const styles = {
  page: {
    ...pageContainer,
  },
  formCard: {
    ...cardBase,
    marginBottom: spacing[5],
  },
  formTitle: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: `0 0 ${spacing[4]}px 0`,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: spacing[4],
    alignItems: "start",
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: spacing[4],
  },
  input: {
    ...inputBase,
    backgroundColor: colors.gray50,
  },
  addCell: {
    display: "flex",
    alignItems: "flex-end",
    alignSelf: "end",
  },
  addBtn: {
    ...btnPrimary,
    justifyContent: "center",
  },
  saveBtn: {
    ...btnPrimary,
  },
  cancelBtn: {
    ...btnSecondary,
  },
  botonDeshabilitado: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  acciones: {
    display: "flex",
    gap: spacing[2],
    alignItems: "center",
  },
  eyeBtn: {
    ...botonIcono,
    background: colors.primaryLighter,
    color: colors.primary,
    border: `1px solid ${colors.infoLight}`,
  },
  editBtn: {
    ...botonIcono,
    background: colors.white,
    color: colors.gray600,
    border: `1px solid ${colors.gray200}`,
  },
  deleteBtn: {
    ...botonIcono,
    background: colors.errorLight,
    color: colors.errorDark,
    border: "none",
  },
  listaEdificios: {
    margin: 0,
    paddingLeft: spacing[5],
    color: colors.gray900,
    fontSize: typography.sizes.md,
    lineHeight: 1.6,
  },
};

export default GestionAdmins;
